import React, { useEffect, useState } from 'react'

import DataSourceDropdown from '@/components/ui/data-source/DataSourceDropdown'
import { useApi, useCanSelectDataSource, useCompany, useDashboard } from '@/hooks'
import useCompanyHook from '@/hooks/useCompany'
import { ConfigControls } from '@/insights/components/ConfigControls'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getInsightDefinition, type InsightKey } from '@/insights/insights'
import type { Asset, InsightDefinition, InsightStatus } from '@/types/dashboard-types'
import { DATA_SOURCE_KEY, type DataSource, DEFAULT_DATA_SOURCE } from '@/types/data-source'

import { AddInsightContext } from './DashboardConfigPanel'

interface ConfigureInsightProps {
  context: AddInsightContext
  setContext: React.Dispatch<React.SetStateAction<AddInsightContext>>
}

export const ConfigureInsight: React.FC<ConfigureInsightProps> = ({ context, setContext }) => {
  const { companyId } = useCompany()
  const canSelectDataSource = useCanSelectDataSource()
  const dashboardContext = useDashboard()
  const { companies } = useCompanyHook()
  const { api } = useApi()
  const dashboardParams = dashboardContext.currentDashboard?.parameters || {}

  const [dashboardAssetName, setDashboardAssetName] = useState<string>('')

  // Resolve asset name for dashboard-level asset
  useEffect(() => {
    if (!api) {
      return
    }
    if (!dashboardParams.assetId || !dashboardParams.companyId) {
      setDashboardAssetName('')
      return
    }
    const fn = async () => {
      const response = await api.get<Asset[]>({
        path: `/assets?companyId=${dashboardParams.companyId}&skip=0&take=10000&deleted=false`,
        origin: 'ConfigureInsight',
      })
      if (!response.error && response.data) {
        const asset = response.data.find((a) => String(a.id) === String(dashboardParams.assetId))
        setDashboardAssetName(asset?.name || String(dashboardParams.assetId))
      }
    }
    fn()
  }, [dashboardParams.assetId, dashboardParams.companyId, api])
  const _definition = getInsightDefinition(context.configuration!.slug as InsightKey)
  type DataType = Exclude<Awaited<ReturnType<typeof _definition.load>>, InsightStatus>
  type ParamsType = Parameters<typeof _definition.configuration.isValid>[0]
  const definition = _definition as InsightDefinition<ParamsType, DataType>
  // Pre-fill dashboard-level params into insight params
  const initialParams = { ...(context.configuration!.parameters as ParamsType) }
  if (dashboardParams.companyId) (initialParams as Record<string, unknown>).companyId = dashboardParams.companyId
  if (dashboardParams.assetId) (initialParams as Record<string, unknown>).assetId = dashboardParams.assetId
  if (dashboardParams.date) (initialParams as Record<string, unknown>).date = dashboardParams.date
  if (dashboardParams.from) (initialParams as Record<string, unknown>).from = dashboardParams.from
  if (dashboardParams.to) (initialParams as Record<string, unknown>).to = dashboardParams.to

  const [parameters, setParameters] = React.useState<ParamsType>({
    ...initialParams,
    title: context.configuration!.title,
  })

  React.useEffect(() => {
    if (definition) {
      // Fill in defaults. For autofill, we will go ahead and save the current values
      // for validation purposes, but they get overridden when the Insight is executed
      const defaults: ParamsType = {}
      definition.configuration.controls.forEach((c) => {
        if (c.options) {
          const defaultValue = c.options.find((o) => o.isDefault)?.value
          if (defaultValue) {
            defaults[c.id] = defaultValue
          }
        }
      })
      setParameters((old: ParamsType) => {
        Object.assign(old, defaults)
        return old
      })
    }
  }, [definition, companyId])

  React.useEffect(() => {
    setContext((previous) => {
      const { title, ...actualParameters } = parameters as ParamsType & { title?: string }
      return {
        ...previous,
        configuration: {
          ...previous.configuration!,
          title: title || definition.name,
          parameters: actualParameters,
        },
      }
    })
  }, [parameters])

  const controls = definition.configuration.controls

  // Build disabled field labels from dashboard params
  const disabledFieldLabels: Record<string, string> = {}
  if (dashboardParams.companyId) {
    const company = companies.find((c) => String(c.id) === String(dashboardParams.companyId))
    disabledFieldLabels.companyId = company?.name || String(dashboardParams.companyId)
  }
  if (dashboardParams.assetId) {
    disabledFieldLabels.assetId = dashboardAssetName || String(dashboardParams.assetId)
  }
  if (dashboardParams.date) {
    if (dashboardParams.date === 'custom') {
      const f = dashboardParams.from ? String(dashboardParams.from).slice(0, 10) : '...'
      const t = dashboardParams.to ? String(dashboardParams.to).slice(0, 10) : '...'
      disabledFieldLabels.date = `Custom: ${f} – ${t}`
      disabledFieldLabels.from = f
      disabledFieldLabels.to = t
    } else {
      const opt = DateOptions.find((o) => o.value === dashboardParams.date)
      disabledFieldLabels.date = opt?.label || String(dashboardParams.date)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto h-full p-4 ">
      <div className="mb-4">
        <div className="mb-4 text-lg">
          Configure: <span className="text-primary">{definition.name}</span>
        </div>
        <label className="block text-base-content mb-2">Title</label>
        <input
          type="text"
          className="input input-bordered text-base border-base-300 w-full focus:outline-none focus:border-primary/30"
          value={parameters.title as string}
          onChange={(e) => setParameters({ ...parameters, title: e.target.value })}
          placeholder={definition.name}
          onFocus={(e) => e.target.select()}
        />
      </div>

      {canSelectDataSource ? (
        <div className="mb-6">
          <DataSourceDropdown
            label="Data Source"
            value={(parameters[DATA_SOURCE_KEY] as DataSource | undefined) ?? DEFAULT_DATA_SOURCE}
            onChange={(value) => setParameters({ ...parameters, [DATA_SOURCE_KEY]: value })}
          />
        </div>
      ) : null}

      {controls.map((control) => (
        <div key={control.id} className="mb-6">
          <ConfigControls
            control={control}
            parameters={parameters}
            disabledFieldLabels={disabledFieldLabels}
            onChange={(params) => {
              let newParams = { ...params }
              if (definition.configuration.transform) {
                newParams = definition.configuration.transform({ parameters: params, field: control.id })
              }
              setParameters(newParams)
            }}
          />
        </div>
      ))}
    </div>
  )
}
